import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { registerHankoElements, getHankoApiUrl } from '../lib/hanko';
import { useAuth } from '../context/authContext';

interface SignUpModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title?: string;
    description?: string;
    primaryButtonText?: string;
    onPrimaryAction?: () => void;
}

const SignUpModal = ({
    open,
    onOpenChange,
    title,
    description,
}: SignUpModalProps) => {
    const { t } = useTranslation();
    const { isAuthenticated } = useAuth();

    const defaultTitle = title || t('signupModal.turnChoresIntoAdventures', 'Turn chores into adventures');
    const defaultDescription = description || t('signupModal.watchKidsBecomeHelpers', 'Watch your kids become your best helpers with interactive checklists and AI-powered characters.');
    const defaultFreeTrialText = t('signupModal.startFreeTrial', 'Start your free trial');

    useEffect(() => {
        const apiUrl = getHankoApiUrl();
        void registerHankoElements(apiUrl).catch(() => undefined);
    }, []);

    useEffect(() => {
        if (isAuthenticated && open) {
            onOpenChange(false);
        }
    }, [isAuthenticated, open, onOpenChange]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md border-none bg-linear-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6 sm:p-8 rounded-3xl shadow-2xl" data-testid="signup-modal">
                <DialogTitle className="sr-only" data-testid="modal-title-sr">
                    {defaultTitle}
                </DialogTitle>
                <DialogDescription className="sr-only" data-testid="modal-description-sr">
                    {defaultDescription}
                </DialogDescription>

                {/* Main content */}
                <div className="text-center space-y-6" data-testid="modal-content">
                    {/* Minifil Logo */}
                    <div className="flex justify-center" data-testid="logo-illustration">
                        <div className="w-20 h-20 sm:w-24 sm:h-24 bg-white dark:bg-gray-100 rounded-2xl flex items-center justify-center shadow-lg dark:shadow-gray-800/50 overflow-hidden">
                            <img
                                src="/minifil-150.png"
                                alt="Minifil Logo"
                                className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                                data-testid="minifil-logo"
                            />
                        </div>
                    </div>

                    {/* Title */}
                    <h2
                        className="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-100 leading-tight"
                        data-testid="modal-title"
                    >
                        {defaultTitle}
                    </h2>

                    {/* Description */}
                    <p
                        className="text-gray-600 dark:text-gray-300 text-sm sm:text-base leading-relaxed"
                        data-testid="modal-description"
                    >
                        {defaultDescription}
                        <br />
                        <span className="font-semibold text-gray-800 dark:text-white">{defaultFreeTrialText}</span>
                    </p>

                    {/* Hanko Elements Auth Web Component */}
                    <div className="w-full text-left">
                        <hanko-auth />
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SignUpModal;
